import React from 'react';
import { Box, InputLabel, Typography } from '@mui/material';
import {
  disabledFormFileInputStyles,
  disabledFormInputStyles,
} from '@Components/Form/styles';
import Flex from '@Components/Bases/Flex';
import FileInputHelper from './FileInputHelper';
import FormFileInput from '@Components/Form/FormFileInput/FormFileInput';
import { FormInput } from '@Components/Form';

type UploadFieldProps = {
  name: string;
  index: number;
  assets: Array<{
    fileNameEn?: string;
    fileNameTh?: string;
    urlEn?: string;
    urlTh?: string;
    isUseSameFile?: boolean;
  }>;
  disabledEn?: boolean;
  disabledTh?: boolean;
  trigger: () => void;
  errors?: any;
  getErrorMessage: (
    errors: any,
    name: string,
    index: number,
    local: string
  ) => string;
  handleFileUpload: (file: File, index: number, local: string) => Promise<void>;
  handleFileCancel: (index: number, local: string) => Promise<void>;
  local: 'Th' | 'En';
};

const UploadField: React.FC<UploadFieldProps> = ({
  name,
  index,
  assets,
  disabledEn,
  disabledTh,
  errors,
  local,
  getErrorMessage,
  handleFileUpload,
  handleFileCancel,
}) => {
  const fileName =
    local === 'En' ? assets[index]?.fileNameEn : assets[index]?.fileNameTh;
  const urlEn = assets[index]?.urlEn;
  const urlTh = assets[index]?.urlTh;

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    const selectedFile = fileInput.files?.[0];

    if (selectedFile) {
      try {
        await handleFileUpload(selectedFile, index, local.toLowerCase());
      } catch (error) {
        console.error('Upload failed', error);
      } finally {
        fileInput.value = '';
      }
    }
  };

  return (
    <>
      <Flex gap={3} mt={2}>
        <InputLabel
          sx={{ width: 1, maxWidth: 350, textAlign: 'right' }}
          required
        >
          {`แนบรูปภาพประกอบเพิ่มเติม (${local === 'En' ? 'อังกฤษ' : 'ไทย'})`}
        </InputLabel>
        {assets[index]?.isUseSameFile ? (
          <Flex flexDirection={'column'} width="340px">
            <FormFileInput
              name={`${name}.assets.${index}.fileName${local}`}
              value={fileName}
              accept=".jpg, .jpeg, .png"
              placeholder={`กรุณาเลือกไฟล์ภาพเพื่ออัปโหลด`}
              helperText={
                <FileInputHelper
                  url={urlTh}
                  helperText={'(ขนาดไม่เกิน 3 MB / .jpeg/ .jpg/ .png)'}
                />
              }
              onChange={handleChange}
              onCancel={async () => {
                await handleFileCancel(index, local.toLowerCase());
              }}
              rules={{
                validate: {
                  maxSize: (value) =>
                    !value ||
                    value.size <= 3 * 1024 * 1024 ||
                    `ไฟล์ภาพมีขนาดใหญ่เกินไป`, // 3 MB
                },
              }}
              sx={{
                ...((local === 'En' &&
                  (disabledEn || assets[index]?.isUseSameFile)) ||
                (local === 'Th' &&
                  (disabledTh || (disabledEn && assets[index]?.isUseSameFile)))
                  ? disabledFormFileInputStyles
                  : {}),
                width: '100%',
                maxWidth: '340px',
              }}
            />
            <Box mt={1}>
              {errors && (
                <Typography fontSize="small" color="error">
                  {getErrorMessage(errors, name, index, local.toLowerCase())}
                </Typography>
              )}
            </Box>
          </Flex>
        ) : (
          <Flex flexDirection={'column'} width="340px">
            <FormFileInput
              name={`${name}.assets.${index}.fileName${local}`}
              value={fileName}
              accept=".jpg, .jpeg, .png"
              placeholder={`กรุณาเลือกไฟล์ภาพเพื่ออัปโหลด`}
              helperText={
                <FileInputHelper
                  url={local === 'En' ? urlEn : urlTh}
                  helperText={'(ขนาดไม่เกิน 3 MB / .jpeg/ .jpg/ .png)'}
                />
              }
              onChange={handleChange}
              onCancel={async () => {
                await handleFileCancel(index, local.toLowerCase());
              }}
              rules={{
                validate: {
                  maxSize: (value) =>
                    !value ||
                    value.size <= 3 * 1024 * 1024 ||
                    `ไฟล์ภาพมีขนาดใหญ่เกินไป`, // 3 MB
                },
              }}
              sx={
                (local === 'En' ? disabledEn : disabledTh)
                  ? {
                      ...disabledFormFileInputStyles,
                      width: '100%',
                      maxWidth: '340px',
                    }
                  : { width: '100%', maxWidth: '340px' }
              }
            />
            <Box mt={1}>
              {errors && (
                <Typography fontSize="small" color="error">
                  {getErrorMessage(errors, name, index, local.toLowerCase())}
                </Typography>
              )}
            </Box>
          </Flex>
        )}
      </Flex>
      {(local === 'Th' && assets[index]?.urlTh) ||
      (local === 'En' && assets[index]?.urlEn) ||
      assets[index]?.isUseSameFile ? (
        <Flex alignItems={'baseline'} gap={3} mt={2}>
          <InputLabel sx={{ width: 1, maxWidth: 350, textAlign: 'right' }}>
            {`ชื่อรูปภาพ/หัวข้อ/คำอธิบายเพิ่มเติม (${
              local === 'Th' ? 'ไทย' : 'อังกฤษ'
            }) (ถ้ามี)`}
          </InputLabel>
          <FormInput
            name={`${name}.assets.${index}.description${local}`}
            placeholder={`กรุณาระบุชื่อรูปภาพ/หัวข้อ/คำอธิบายเพิ่มเติม (${
              local === 'Th' ? 'ไทย' : 'อังกฤษ'
            })`}
            sx={{
              ...(local === 'Th' && disabledTh ? disabledFormInputStyles : {}),
              ...(local === 'En' && disabledEn ? disabledFormInputStyles : {}),
              width: '100%',
              maxWidth: '340px',
            }}
          />
        </Flex>
      ) : null}
    </>
  );
};

export default UploadField;
